import type { IndustryPage } from '../industries';

const lines = [
  { item: 'Deep clean, 3 bed / 2 bath house, about 1,800 sq ft, kitchen degreased, baseboards hand-wiped', amount: '$320' },
  { item: 'Inside all kitchen cabinets and drawers (emptied by the customer)', amount: '$70' },
  { item: 'Inside windows and sills, 18 windows', amount: '$80' },
  { item: 'Optional: inside the oven and the refrigerator', amount: '$60' },
];

const faq = [
  {
    q: 'What is the difference between a deep clean and a standard clean on a quote?',
    a: 'Spell it out. A deep clean usually adds baseboards, door frames, light switches, cabinet fronts, grout scrubbing and built-up grease, while a standard clean maintains surfaces that are already in good shape. Listing the tasks avoids the "I thought that was included" call.',
  },
  {
    q: 'What should a cleaning cancellation policy cover?',
    a: 'A notice period for cancelling or rescheduling, what you charge for a late cancellation or a lockout, and what happens to any deposit. Save it in your clause library so it is on every quote the customer accepts, instead of buried on your website.',
  },
  {
    q: 'My clients speak Spanish. Can they read the quote in Spanish?',
    a: 'Yes. Set the client\'s language to Spanish, English or Brazilian Portuguese, and the quote they open, and its PDF, appear in that language. The app itself also runs in English or Spanish for you and your office.',
  },
  {
    q: 'Can a customer add the oven or the fridge themselves?',
    a: 'Yes. Put those as optional lines. The customer ticks the extras on the quote page and the total updates before they accept.',
  },
];

const page: IndustryPage = {
  slug: 'cleaning',
  order: 2,
  group: 'Cleaning',
  tile: 'cleaning',
  path: '/for/cleaning',
  primaryKeyword: 'cleaning business software',
  secondaryKeywords: ['house cleaning quote software', 'cleaning service cancellation policy', 'maid service software', 'move-out cleaning quote'],
  title: 'Cleaning Business Software for Quotes & Invoices | Scopie', // 57
  metaDescription:
    'Quote deep cleans and move-outs task by task, let clients tick extras like the oven, and put your lockout and cancellation policy on every quote.', // 145
  h1: 'Quotes for deep cleans, move-outs and one-off jobs',
  answerFirst:
    'Scopie helps house cleaning companies quote deep cleans and move-outs task by task, so the client sees what is included and can add the oven or fridge with a tick. Your lockout and cancellation policy is on the quote they accept, and clients who prefer Spanish or Portuguese can read it in their own language.',
  exampleQuote: { label: 'Sample deep clean quote', lines, total: '$530', depositNote: 'Lockout and cancellation wording is the cleaning company\'s own clause.' },
  blocks: [
    {
      type: 'text',
      id: 'lockouts',
      h2: 'Lockouts and late cancellations, settled before the first visit',
      lead: 'The team drove forty minutes, the code did not work, and nobody answered the phone.',
      paragraphs: [
        'Last-minute cancellations and locked doors are the quiet profit killer in residential cleaning. A policy on your website is easy to miss. The same policy on the quote, as part of what the client accepts, is a different conversation when the lockout fee comes up.',
        'Write your notice period, lockout charge and rescheduling rule once in your clause library, and every quote carries it.',
      ],
    },
    {
      type: 'sample-quote',
      id: 'sample',
      h2: 'A deep clean with the extras listed',
      label: 'Sample quote: first-time deep clean',
      lines,
      total: '$530',
      totalLabel: 'Total with oven and fridge',
      note: 'Example figures. Oven and fridge are an optional line the client can tick.',
    },
    {
      type: 'list',
      id: 'move-out',
      h2: 'Move-out cleans: what to confirm before quoting',
      style: 'checklist',
      items: [
        { text: 'Will the power and water still be on on cleaning day?' },
        { text: 'Is the home empty, or will boxes and furniture still be there?' },
        { text: 'Does the landlord or agent have a checklist the clean must pass?' },
        { text: 'Who lets the team in, and where is the key left afterwards?' },
        { text: 'Are walls, blinds, the garage or the patio part of it?' },
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Where cleaning quotes lose money',
      style: 'mistakes',
      items: [
        { title: 'Condition priced from a phone call', text: 'A first deep clean after years without one can take twice as long. Ask for photos or walk it.' },
        { title: 'Pets not mentioned', text: 'Heavy shedding adds time to every room. Ask, and price it.' },
        { title: 'Supplies unassigned', text: 'Say whether you bring products and equipment, or use the client\'s.' },
        { title: 'Extras done as favors', text: 'Inside the oven, fridge and cabinets are real work. Offer them as lines.' },
        { title: 'No lockout clause', text: 'Without it, a wasted trip is your loss.' },
      ],
    },
    {
      type: 'text',
      id: 'teams',
      h2: 'Teams that know where to go, and invoices that follow',
      paragraphs: [
        'Accepted quotes become visits on your schedule with the team assigned. Cleaners see their addresses, times and entry notes, but not what each home pays, which matters in a trade where clients sometimes try to hire your staff directly.',
        'When the clean is done, share the invoice link. Repeat clients stay on your client list with every past quote, so the spring deep clean is priced from last year\'s.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Cleaning business questions', items: faq },
  ],
  sections: [],
  faq,
  internalLinks: [
    { href: '/for', anchor: 'All industries', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for/pressure-washing', anchor: 'Scopie for pressure washing businesses', status: 'live' },
    { href: '/for/landscaping', anchor: 'Scopie for landscaping businesses', status: 'live' },
    { href: '/templates/cancellation-policy', anchor: 'free cleaning cancellation policy template', status: 'live' },
  ],
  cta: {
    heading: 'Put your lockout policy where clients read it',
    text: 'Set up your cleaning tasks, extras and policy in Scopie, and quote the next deep clean in a few minutes.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [],
};

export default page;
